import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import authService from '../service/AuthService';
import createAxiosInstance from '../support/AxiosSupport';

const STORAGE_KEY = 'clothes_selected_wardrobe_owner';
const STORAGE_KEY_DISPLAY = 'clothes_selected_wardrobe_owner_name';

export const ALL_PERMISSIONS = [
    'VIEW_ITEMS', 'VIEW_THROWN_AWAY', 'EDIT_ITEMS', 'ADD_ITEMS', 'MOVE_TO_TRASH',
    'THROW_AWAY_IMMEDIATE', 'CLEAR_TRASH', 'VIEW_LISTS', 'MANAGE_LIST_ITEMS', 'MANAGE_LISTS',
    'ADD_COMMENTS'
] as const;

export type WardrobePermission = typeof ALL_PERMISSIONS[number];

export function hasPermission(permissions: string[] | null | undefined, perm: WardrobePermission): boolean {
    if (!permissions || permissions.length === 0) return false;
    return permissions.includes(perm);
}

interface WardrobeContextValue {
    selectedOwnerId: string | null;
    selectedOwnerDisplayName: string | null;
    permissions: string[] | null;
    setSelectedOwnerId: (id: string | null, displayName?: string | null, permissions?: string[] | null) => void;
    isOwnWardrobe: boolean;
}

const WardrobeContext = createContext<WardrobeContextValue | null>(null);

export const WardrobeProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
    const [selectedOwnerId, setSelectedOwnerIdState] = useState<string | null>(null);
    const [selectedOwnerDisplayName, setSelectedOwnerDisplayNameState] = useState<string | null>(null);
    const [permissions, setPermissionsState] = useState<string[] | null>(null);

    const sub = authService.getUserInfo()?.sub;
    const isOwnWardrobe = !selectedOwnerId || (sub && selectedOwnerId === sub);

    useEffect(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        const storedName = localStorage.getItem(STORAGE_KEY_DISPLAY);
        if (stored && stored !== 'null' && stored !== '') {
            setSelectedOwnerIdState(stored);
            setSelectedOwnerDisplayNameState(storedName || null);
        }
    }, []);

    useEffect(() => {
        if (!selectedOwnerId || selectedOwnerDisplayName) return;
        let cancelled = false;
        createAxiosInstance().then(api =>
            api.get<{ id: string; username?: string; name?: string }>(`/users/${selectedOwnerId}`)
        ).then(res => {
            if (cancelled) return;
            const u = res.data;
            const name = u?.name || u?.username;
            if (name) {
                setSelectedOwnerDisplayNameState(name);
                localStorage.setItem(STORAGE_KEY_DISPLAY, name);
            }
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [selectedOwnerId, selectedOwnerDisplayName]);

    useEffect(() => {
        if (!selectedOwnerId || isOwnWardrobe) return;
        let cancelled = false;
        createAxiosInstance().then(api =>
            api.get<string[]>('/wardrobe-access/my-permissions', {params: {ownerId: selectedOwnerId}})
        ).then(res => {
            if (!cancelled && res.data) {
                setPermissionsState(res.data);
            }
        }).catch(() => {
            if (!cancelled) setPermissionsState([]);
        });
        return () => { cancelled = true; };
    }, [selectedOwnerId, isOwnWardrobe]);

    const setSelectedOwnerId = useCallback((id: string | null, displayName?: string | null, perms?: string[] | null) => {
        setSelectedOwnerIdState(id);
        const name = displayName ?? null;
        setSelectedOwnerDisplayNameState(name);
        if (id) {
            localStorage.setItem(STORAGE_KEY, id);
            if (name) localStorage.setItem(STORAGE_KEY_DISPLAY, name);
            else localStorage.removeItem(STORAGE_KEY_DISPLAY);
            if (perms) {
                setPermissionsState(perms);
            } else {
                setPermissionsState(null);
            }
        } else {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(STORAGE_KEY_DISPLAY);
            setPermissionsState(null);
        }
    }, []);

    const effectivePermissions = isOwnWardrobe ? ALL_PERMISSIONS as unknown as string[] : permissions;

    return (
        <WardrobeContext.Provider value={{
            selectedOwnerId,
            selectedOwnerDisplayName,
            permissions: effectivePermissions ?? null,
            setSelectedOwnerId,
            isOwnWardrobe
        }}>
            {children}
        </WardrobeContext.Provider>
    );
};

export const useWardrobe = (): WardrobeContextValue => {
    const ctx = useContext(WardrobeContext);
    if (!ctx) {
        return {
            selectedOwnerId: null,
            selectedOwnerDisplayName: null,
            permissions: [...ALL_PERMISSIONS],
            setSelectedOwnerId: () => {},
            isOwnWardrobe: true
        };
    }
    return ctx;
};
